<template>
  <b-modal
    id="operational-procedure-wizard"
    size="lg"
    :title="isPrefillMode ? $t('simulacrum.start_form') : $t('operational_procedure_exercise.new')"
    no-close-on-backdrop
    hide-footer
    @show="onModalShow"
    @hide="onModalHide"
  >
    <validation-observer ref="validationObserver">
      <b-form>
        <b-progress :value="(currentStep / totalSteps) * 100" height="2px" class="mb-2" />

        <div v-show="currentStep === 1">
          <div class="mb-3">
            <h5 class="mb-2 wizard-title">{{ $t('operational_procedure.reference_number') }}</h5>
            <b-form-input v-model="referenceNumber" class="mb-2" />
          </div>

          <h5 class="mb-2 wizard-title">{{ $t('unit.single') }}<span class="text-danger">*</span></h5>
          <v-select
            v-model="selectedUnit"
            :options="unitOptions"
            :get-option-label="getUnitLabel"
            :reduce="(option) => option"
            :placeholder="$t('unit.single')"
            :clearable="true"
            :loading="isLoadingUnits"
            :filterable="false"
            @search="onUnitSearch"
            class="mb-2"
            :class="{ 'is-invalid': showError }"
          />
          <div class="d-flex flex-column flex-md-row">
            <div class="mr-md-2 mb-2 mb-md-0" style="flex:1;">
              <h5 class="mb-1 wizard-title">REMS</h5>
              <b-form-input
                v-model.number="selectedRems"
                type="number"
                step="1"
                :placeholder="selectedUnit && (selectedUnit.rems === null || selectedUnit.rems === undefined) ? 'No REMS' : 'REMS'"
                :class="{ 'is-invalid': selectedUnit && !isConsistent && isRemsMismatch }"
                disabled
              />
            </div>
            <div style="flex:1;">
              <h5 class="mb-1 wizard-title">SIRH</h5>
              <b-form-input
                v-model.number="selectedSirh"
                type="number"
                step="1"
                :placeholder="selectedUnit && (selectedUnit.sirh === null || selectedUnit.sirh === undefined) ? 'No SIRH' : 'SIRH'"
                :class="{ 'is-invalid': selectedUnit && !isConsistent && isSirhMismatch }"
                disabled
              />
            </div>
          </div>
          <small v-if="showError" class="text-danger">{{ $t('validation.required', { field: $t('unit.single') }) }}</small>
        </div>

        <div v-show="currentStep === 2">
          <simulacrum-assessment-step
            v-if="selectedUnit"
            :key="(selectedUnit && selectedUnit.id) || (selectedUnitData && selectedUnitData.id_tenant) || 'no-unit'"
            :prop-item-data="{ ...selectedUnitData, reference_number: referenceNumber }"
            :existing-operational-procedure-id="isPrefillMode ? prefill.operationalProcedureId : null"
            :existing-record-last-status-name="isPrefillMode ? prefill.existingRecordLastStatusName : null"
            @open-simulacrum-modal="handleOpen"
          />
        </div>

        <div class="d-flex justify-content-between mt-2">
          <b-button v-if="currentStep > 1 && !isPrefillMode" variant="outline-secondary" @click="currentStep--">{{ $t('actions.previous') }}</b-button>
          <b-button v-if="currentStep < totalSteps" variant="primary" @click="nextStep" :disabled="!canProceed">{{ $t('actions.next') }}</b-button>
        </div>
      </b-form>
    </validation-observer>
  </b-modal>
</template>

<script>
import { ref, computed, watch } from "@vue/composition-api";
import { ValidationObserver } from 'vee-validate';
import vSelect from 'vue-select';
import axios from '@/libs/axios';
import SimulacrumAssessmentStep from './SimulacrumAssessmentStep.vue';
import { useToast } from 'vue-toastification/composition';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import { useMainUnitWizardOptions } from '@/libs/risk/useMainUnitWizardOptions';

export default {
  name: 'OperationalProcedureWizard',
  components: { ValidationObserver, vSelect, SimulacrumAssessmentStep },
  props: {
    prefill: {
      type: Object,
      default: null,
    },
  },
  setup(props, { emit, root }) {
    const toast = useToast();
    const currentStep = ref(1);
    const totalSteps = 2;
    const selectedUnit = ref(null);
    const selectedRems = ref(null);
    const selectedSirh = ref(null);
    const validationObserver = ref(null);
    const showError = ref(false);

    const generateReferenceNumber = () => {
      const year = new Date().getFullYear().toString().slice(-2);
      const millis = Date.now().toString().slice(-6).padStart(6, '0');
      return `EO-${year}-${millis}`;
    };

    const referenceNumber = ref(generateReferenceNumber());
    const userData = JSON.parse(localStorage.getItem("userData"));
    const checking = ref(false);
    const { isLoadingUnits, unitOptions, fetchMainUnits, onUnitSearch, resolveUnitById } =
      useMainUnitWizardOptions(userData);

    const getUnitLabel = (option) => {
      if (!option) return '';
      const remsText = option.rems === null || option.rems === undefined || option.rems === '' ? 'No REMS' : `REMS: ${option.rems}`;
      const sirhText = option.sirh === null || option.sirh === undefined || option.sirh === '' ? 'No SIRH' : `SIRH: ${option.sirh}`;
      return `${option.description} (${remsText}, ${sirhText})`;
    };

    const isConsistent = computed(() => {
      if (!selectedUnit.value) return false;
      const unitRems = selectedUnit.value.rems;
      const unitSirh = selectedUnit.value.sirh;
      const inputRems = selectedRems.value;
      const inputSirh = selectedSirh.value;

      const remsMatches = (unitRems === null || unitRems === undefined || unitRems === '')
        ? (inputRems === null || inputRems === undefined || inputRems === '')
        : Number(unitRems) === Number(inputRems);

      const sirhMatches = (unitSirh === null || unitSirh === undefined || unitSirh === '')
        ? (inputSirh === null || inputSirh === undefined || inputSirh === '')
        : Number(unitSirh) === Number(inputSirh);

      return remsMatches && sirhMatches;
    });

    const isRemsMismatch = computed(() => {
      if (!selectedUnit.value) return false;
      const unitRems = selectedUnit.value.rems;
      if (unitRems === null || unitRems === undefined || unitRems === '') {
        return !(selectedRems.value === null || selectedRems.value === undefined || selectedRems.value === '');
      }
      return Number(unitRems) !== Number(selectedRems.value);
    });

    const isSirhMismatch = computed(() => {
      if (!selectedUnit.value) return false;
      const unitSirh = selectedUnit.value.sirh;
      if (unitSirh === null || unitSirh === undefined || unitSirh === '') {
        return !(selectedSirh.value === null || selectedSirh.value === undefined || selectedSirh.value === '');
      }
      return Number(unitSirh) !== Number(selectedSirh.value);
    });

    const canProceed = computed(() => currentStep.value === 1 ? (!!selectedUnit.value && !checking.value && isConsistent.value) : true);
    const selectedUnitData = computed(() => {
      if (!selectedUnit.value) return null;
      const unitTenant = selectedUnit.value.router?.id_tenant || null;
      return { id: selectedUnit.value.id, id_tenant: unitTenant };
    });

    const checkActiveOperationalProcedure = async (unitId, { silent = false } = {}) => {
      try {
        checking.value = true;
        const { data } = await axios.get('/v1/operational_procedure', { params: { id_unit: unitId, with: ['last_status'] } });
        const exists = !!data?.exists;
        if (exists) {
          const ref = data?.[0]?.reference_number || '';
          if (!silent) {
            const title = ref ? `Ya existe un procedimiento en progreso (Ref: ${ref})` : 'Ya existe un procedimiento en progreso para esta instalación';
            toast({ component: ToastificationContent, props: { title, icon: 'AlertTriangleIcon', variant: 'warning' } });
          }
          return { exists: true, ref };
        }
        return { exists: false };
      } catch (_) { return { exists: false }; }
      finally { checking.value = false; }
    };

    const nextStep = async () => {
      if (currentStep.value === 1 && !selectedUnit.value) {
        showError.value = true;
        return;
      }
      showError.value = false;

      if (currentStep.value === 1) {
        const { exists } = await checkActiveOperationalProcedure(selectedUnit.value.id);
        if (exists) return; // block
      }

      if (currentStep.value < totalSteps) currentStep.value++;
    };

    const handleOpen = ({ id, snapshot, sources }) => {
      root.$bvModal.hide('operational-procedure-wizard');
      emit('open-simulacrum-modal', { id, snapshot, sources });
    };

    const resetWizardState = () => {
      currentStep.value = 1;
      selectedUnit.value = null;
      selectedRems.value = null;
      selectedSirh.value = null;
      showError.value = false;
      referenceNumber.value = generateReferenceNumber();
      validationObserver.value?.reset();
    };

    const isPrefillMode = computed(() => {
      const p = props.prefill;
      return !!(p && p.operationalProcedureId != null && p.unitId != null);
    });

    const onModalShow = async () => {
      resetWizardState();
      await fetchMainUnits();
      const p = props.prefill;
      if (!p || p.operationalProcedureId == null || p.unitId == null) return;
      const uid = Number(p.unitId);
      let unit =
        p.unitSnapshot && Number(p.unitSnapshot.id) === uid ? p.unitSnapshot : null;
      if (!unit) {
        unit = await resolveUnitById(uid);
      }
      if (!unit) {
        toast({
          component: ToastificationContent,
          props: {
            title: "No se encontró la instalación en el catálogo.",
            icon: "AlertTriangleIcon",
            variant: "warning",
          },
        });
        return;
      }
      referenceNumber.value = p.referenceNumber || "";
      selectedUnit.value = unit;
      currentStep.value = 2;
    };

    const onModalHide = () => {
      resetWizardState();
      emit("wizard-closed");
    };

    const isUpdatingFromUnit = ref(false);

    watch(selectedUnit, (unit) => {
      if (!unit) {
        selectedRems.value = null;
        selectedSirh.value = null;
        return;
      }
      isUpdatingFromUnit.value = true;
      selectedRems.value = unit.rems !== null && unit.rems !== undefined && unit.rems !== '' ? Number(unit.rems) : null;
      selectedSirh.value = unit.sirh !== null && unit.sirh !== undefined && unit.sirh !== '' ? Number(unit.sirh) : null;
      setTimeout(() => {
        isUpdatingFromUnit.value = false;
      }, 0);
    });

    watch(selectedRems, (val) => {
      if (isUpdatingFromUnit.value) return;

      if (!selectedUnit.value && val !== null && val !== undefined && val !== '' && Number.isFinite(Number(val))) {
        const target = Number(val);
        const match = unitOptions.value.find(u => u && Number(u.rems) === target);
        if (match) {
          selectedUnit.value = match;
        }
      }
    });

    watch(selectedSirh, (val) => {
      if (isUpdatingFromUnit.value) return;

      if (!selectedUnit.value && val !== null && val !== undefined && val !== '' && Number.isFinite(Number(val))) {
        const target = Number(val);
        const match = unitOptions.value.find(u => u && Number(u.sirh) === target);
        if (match) {
          selectedUnit.value = match;
        }
      }
    });

    return {
      currentStep,
      totalSteps,
      selectedUnit,
      unitOptions,
      canProceed,
      referenceNumber,
      selectedUnitData,
      nextStep,
      onModalShow,
      onModalHide,
      validationObserver,
      showError,
      handleOpen,
      selectedRems,
      selectedSirh,
      getUnitLabel,
      isConsistent,
      isRemsMismatch,
      isSirhMismatch,
      isPrefillMode,
      isLoadingUnits,
      onUnitSearch,
    };
  },
};
</script>


